/**
 * 기존 확정 처방과의 교차 이미지 중복 의약품 확인 모달
 */
import type {
  DuplicateFinalizedPrescriptionDrug,
  PrescriptionDrugInfoResponse,
} from '@/entities/prescription/api/prescription.api';
import {
  useDeletePrescriptionDrug,
  useFinalizePrescription,
  useUpdatePrescriptionDrug,
} from '@/entities/prescription/hooks/usePrescriptions';
import {
  buildDuplicateDrugPatchPayload,
  getAffectedCrossImageDuplicateCodes,
  getDuplicateDrugCodes,
  groupDuplicateDrugsByCode,
  isCrossImageDuplicateQuantityChanged,
} from '@/pages/prescriptions/prescriptionDuplicateUtils';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import React, { useEffect, useMemo, useState } from 'react';

interface CrossImageDuplicateModalProps {
  open: boolean;
  onClose: () => void;
  items: DuplicateFinalizedPrescriptionDrug[];
  currentPrescriptionImageCode: string;
  localDrugs: PrescriptionDrugInfoResponse[];
  refYm: string;
  onFinalized: () => void;
}

export const CrossImageDuplicateModal: React.FC<CrossImageDuplicateModalProps> = ({
  open,
  onClose,
  items,
  currentPrescriptionImageCode,
  localDrugs,
  refYm,
  onFinalized,
}) => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const updateDrug = useUpdatePrescriptionDrug();
  const deleteDrug = useDeletePrescriptionDrug();
  const finalize = useFinalizePrescription();
  const [rows, setRows] = useState<DuplicateFinalizedPrescriptionDrug[]>([]);
  const [snapshot, setSnapshot] = useState<DuplicateFinalizedPrescriptionDrug[]>(
    [],
  );
  const [deletedMrIds, setDeletedMrIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const cloned = structuredClone(items);
    setRows(cloned);
    setSnapshot(cloned);
    setDeletedMrIds([]);
  }, [open, items]);

  const drugCodes = useMemo(() => getDuplicateDrugCodes(rows), [rows]);
  const grouped = useMemo(() => groupDuplicateDrugsByCode(rows), [rows]);

  const handleQuantityChange = (
    mrId: number | undefined,
    field: 'inHospitalQuantity' | 'outHospitalQuantity',
    value: string,
  ) => {
    if (mrId == null) return;
    // 정정용 음수 수량 허용. 숫자·마이너스·소수점만 남긴다.
    const cleaned = value.replace(/[^\d.-]/g, '');
    const parsed = Number(cleaned);
    const next = Number.isFinite(parsed) ? parsed : 0;
    setRows((prev) =>
      prev.map((item) =>
        item.mrId === mrId ? { ...item, [field]: next } : item,
      ),
    );
  };

  const handleDelete = async (mrId: number | undefined) => {
    if (mrId == null) return;
    const ok = await confirm({
      title: '삭제 확인',
      description: '이 중복 의약품 행을 삭제하시겠습니까?',
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteDrug.mutateAsync(mrId);
      setDeletedMrIds((prev) =>
        prev.includes(mrId) ? prev : [...prev, mrId],
      );
      toast.success('삭제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleConfirmFinalize = async () => {
    setSubmitting(true);
    try {
      const snapshotByMrId = new Map(snapshot.map((item) => [item.mrId, item]));
      const modified = rows.filter((item) => {
        if (item.mrId == null || deletedMrIds.includes(item.mrId)) return false;
        return isCrossImageDuplicateQuantityChanged(
          item,
          snapshotByMrId.get(item.mrId),
        );
      });

      for (const item of modified) {
        if (item.mrId == null) continue;
        const localDrug = localDrugs.find(
          (drug) => drug.prescriptionMrId === item.mrId,
        );
        await updateDrug.mutateAsync({
          prescriptionMrId: item.mrId,
          data: buildDuplicateDrugPatchPayload(item, localDrug),
          prescriptionImageCode: item.prescriptionImageCode,
          refYm,
        });
      }

      const codesToFinalize = getAffectedCrossImageDuplicateCodes(
        currentPrescriptionImageCode,
        deletedMrIds,
        snapshot,
        modified,
      );

      for (const code of codesToFinalize) {
        await finalize.mutateAsync(code);
      }

      toast.success('처방 내역이 확정되었습니다.');
      onClose();
      onFinalized();
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>기존 확정 처방 중복 의약품 확인</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          다른 처방 이미지에 이미 확정된 동일 의약품이 있습니다. 수량을 조정하거나
          삭제한 뒤 확정해 주세요.
        </p>

        {drugCodes.length === 0 ? (
          <p className="text-sm text-muted-foreground">중복 처방 의약품 없음</p>
        ) : (
          <div className="space-y-6">
            {drugCodes.map((code, index) => {
              const group = grouped.get(code) ?? [];
              const first = group[0];
              return (
                <div key={code} className="space-y-3">
                  {index > 0 ? <hr /> : null}
                  <p className="font-medium">
                    중복 의약품 {index + 1}: {code} — {first?.drugName ?? '-'}
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>이미지 코드</TableHead>
                        <TableHead>원내</TableHead>
                        <TableHead>원외</TableHead>
                        <TableHead className="w-20">작업</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.map((row) => {
                        const isDeleted =
                          row.mrId != null && deletedMrIds.includes(row.mrId);
                        return (
                          <TableRow
                            key={`${row.mrId}-${row.prescriptionImageCode}`}
                            className={isDeleted ? 'opacity-50' : undefined}
                          >
                            <TableCell>
                              {row.prescriptionImageCode}
                              {row.prescriptionImageCode ===
                              currentPrescriptionImageCode
                                ? ' (현재)'
                                : ''}
                            </TableCell>
                            <TableCell>
                              {isDeleted ? (
                                row.inHospitalQuantity
                              ) : (
                                <Input
                                  className="h-8 w-20"
                                  value={String(row.inHospitalQuantity ?? 0)}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      row.mrId,
                                      'inHospitalQuantity',
                                      e.target.value,
                                    )
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {isDeleted ? (
                                row.outHospitalQuantity
                              ) : (
                                <Input
                                  className="h-8 w-20"
                                  value={String(row.outHospitalQuantity ?? 0)}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      row.mrId,
                                      'outHospitalQuantity',
                                      e.target.value,
                                    )
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {isDeleted ? (
                                <span className="text-xs text-muted-foreground">
                                  삭제됨
                                </span>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => void handleDelete(row.mrId)}
                                >
                                  삭제
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            취소
          </Button>
          <Button onClick={handleConfirmFinalize} disabled={submitting}>
            확인 후 확정
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
